import {
  answerQuestionFromDataset,
  createAnalyticsSnapshot,
  type UploadedDataset,
} from './analytics';

export const GEMINI_MODEL = 'gemini-3.6-flash';

interface GeminiGenerateContentResponse {
  candidates?: Array<{
    content?: {
      parts?: Array<{
        text?: string;
      }>;
    };
    finishReason?: string;
  }>;
  promptFeedback?: {
    blockReason?: string;
  };
  error?: {
    message?: string;
  };
}

const DEFAULT_MAX_OUTPUT_TOKENS = 2200;
const CONTINUATION_MAX_OUTPUT_TOKENS = 1200;

export const askGeminiFlashAboutDataset = async (
  question: string,
  dataset: UploadedDataset | null,
) => {
  if (!dataset) {
    return answerQuestionFromDataset(question, dataset);
  }

  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  if (!apiKey) {
    return answerQuestionFromDataset(question, dataset);
  }

  const prompt = buildGeminiPrompt(question, dataset);
  const result = await requestGeminiContent(apiKey, prompt, DEFAULT_MAX_OUTPUT_TOKENS);
  let answer = getGeminiText(result);

  if (!answer) {
    throw new Error('Gemini returned an empty answer.');
  }

  if (isMaxTokenResponse(result)) {
    const continuationResult = await requestGeminiContent(
      apiKey,
      buildContinuationPrompt(prompt, answer),
      CONTINUATION_MAX_OUTPUT_TOKENS,
    );
    const continuation = getGeminiText(continuationResult);

    if (continuation) {
      answer = `${answer.trim()}\n\n${continuation.trim()}`;
    }
  }

  return answer;
};

const requestGeminiContent = async (
  apiKey: string,
  prompt: string,
  maxOutputTokens: number,
) => {
  const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': apiKey,
    },
    body: JSON.stringify({
      contents: [
        {
          role: 'user',
          parts: [
            {
              text: prompt,
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0.2,
        topP: 0.9,
        maxOutputTokens,
      },
    }),
  });

  const result = await parseGeminiResponse(response);

  if (!response.ok) {
    throw new Error(formatGeminiError(result.error?.message));
  }

  if (result.promptFeedback?.blockReason) {
    throw new Error(`Gemini could not answer because the prompt was blocked: ${result.promptFeedback.blockReason}.`);
  }

  return result;
};

const buildGeminiPrompt = (question: string, dataset: UploadedDataset) => {
  const snapshot = createAnalyticsSnapshot(dataset);
  const context = {
    fileName: dataset.fileName,
    rowCount: dataset.rows.length,
    columns: dataset.profiles.map((profile) => ({
      name: profile.name,
      type: profile.type,
      role: profile.role,
      sampleValues: profile.sampleValues,
      uniqueCount: profile.uniqueCount,
      emptyCount: profile.emptyCount,
    })),
    selectedMetric: snapshot.metricColumn?.name,
    selectedDateColumn: snapshot.dateColumn?.name,
    selectedCategoryColumn: snapshot.categoryColumn?.name,
    currency: snapshot.currency,
    metricCards: snapshot.metrics,
    previousPeriodComparison: snapshot.comparison,
    topCategories: snapshot.categorySeries,
    monthlyTrend: snapshot.timeSeries,
    numericColumnTotals: snapshot.numericBreakdown,
    localSummary: snapshot.summary,
    rowSampleLimit: 120,
    rowSample: dataset.rows.slice(0, 120),
  };

  return `
You are GabMic, an AI business-data analytics assistant for businesses of any size and industry.

Rules:
- Use only the uploaded dataset context below.
- Do not invent customers, revenue, expenses, trends, causes, or recommendations.
- If the question asks for something that is not present in the context, say what is missing.
- Keep the answer clear enough for a small-business owner.
- Give concise business analysis with exact numbers from the computed context when available.
- Format the answer with clean Markdown headings, bullets, and tables when useful.
- Finish with a short business meaning or next check so the answer feels complete.
- The row sample may not contain every row. Treat computed metric cards, comparisons, category totals, and trend totals as the source of truth for whole-dataset answers.

Uploaded dataset context:
${JSON.stringify(context, null, 2)}

User question:
${question}
`.trim();
};

const buildContinuationPrompt = (originalPrompt: string, partialAnswer: string) => {
  return `
${originalPrompt}

You already started this GabMic answer, but it was cut off:
${partialAnswer}

Continue from exactly where the answer stopped.
Do not repeat the earlier text.
Keep using Markdown.
Finish the answer completely.
`.trim();
};

const parseGeminiResponse = async (response: Response): Promise<GeminiGenerateContentResponse> => {
  const body = await response.text();
  if (!body) return {};

  try {
    return JSON.parse(body) as GeminiGenerateContentResponse;
  } catch {
    return {
      error: {
        message: body,
      },
    };
  }
};

const getGeminiText = (result: GeminiGenerateContentResponse) => {
  return result.candidates
    ?.flatMap((candidate) => candidate.content?.parts ?? [])
    .map((part) => part.text ?? '')
    .join('\n')
    .trim() ?? '';
};

const isMaxTokenResponse = (result: GeminiGenerateContentResponse) => {
  return result.candidates?.some((candidate) => candidate.finishReason === 'MAX_TOKENS') ?? false;
};

const formatGeminiError = (message?: string) => {
  const fallback = 'Gemini could not answer this question right now.';
  if (!message) return fallback;

  if (/api key not valid|api_key_invalid/i.test(message)) {
    return 'Gemini is configured, but VITE_GEMINI_API_KEY is not valid for the Gemini API. Create a Gemini API key in Google AI Studio and update your .env file.';
  }

  if (/quota|rate limit|resource exhausted/i.test(message)) {
    return 'Gemini reached a quota or rate limit. GabMic will show a local answer from the uploaded data for now.';
  }

  return `${fallback} ${message}`;
};
